import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { FaTimes, FaSave } from "react-icons/fa";
import { ROLES } from "./users.constants";
import {
  getAllAdminPermissionModules,
  getUserAdminPermissions,
} from "../../../services/adminPermissionsServices";

const UserModal = ({ isOpen, onClose, onSubmit, user }) => {
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");

  const [availableModules, setAvailableModules] = useState([]);
  const [selectedModuleKeys, setSelectedModuleKeys] = useState([]);
  const [loadingModules, setLoadingModules] = useState(false);

  const dialogRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    let mounted = true;

    const initModal = async () => {
      setNom(user?.nom || "");
      setPrenom(user?.prenom || "");
      setEmail(user?.email || "");
      setRole(user?.role_id ? String(user.role_id) : "");
      setSelectedModuleKeys([]);
      setLoadingModules(true);

      try {
        const modules = await getAllAdminPermissionModules();
        if (!mounted) return;

        setAvailableModules(Array.isArray(modules) ? modules : []);

        if (user?.id) {
          const permissions = await getUserAdminPermissions(user.id);

          if (!mounted) return;

          const keys = (Array.isArray(permissions) ? permissions : [])
            .filter((perm) => perm?.can_view || perm?.can_edit)
            .map((perm) => perm.module_key);

          setSelectedModuleKeys(keys);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des modules :", error);
      } finally {
        if (mounted) setLoadingModules(false);
      }
    };

    initModal();

    const root = document.documentElement;
    const prev = root.style.overflow;
    root.style.overflow = "hidden";

    const onEsc = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onEsc);

    const t = setTimeout(() => {
      dialogRef.current?.querySelector("input[name='nom']")?.focus();
    }, 0);

    return () => {
      mounted = false;
      root.style.overflow = prev;
      window.removeEventListener("keydown", onEsc);
      clearTimeout(t);
    };
  }, [isOpen, user, onClose]);

  if (!isOpen) return null;

  const isAdmin = Number(role) === 1;

  const toggleModule = (moduleKey) => {
    setSelectedModuleKeys((prev) => {
      if (prev.includes(moduleKey)) {
        return prev.filter((key) => key !== moduleKey);
      }
      return [...prev, moduleKey];
    });
  };

  const handleSelectAllModules = () => {
    setSelectedModuleKeys(
      availableModules.map((module) => module.key || module.module_key)
    );
  };

  const handleUnselectAllModules = () => {
    setSelectedModuleKeys([]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!nom || !prenom || !email || !role) return;

    const adminPermissions = isAdmin
      ? []
      : selectedModuleKeys.map((moduleKey) => ({
          module_key: moduleKey,
          can_view: true,
          can_edit: true,
        }));

    onSubmit({
      nom,
      prenom,
      email,
      role_id: parseInt(role, 10),
      adminPermissions,
    });
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
      role="dialog"
      aria-modal="true"
      aria-label="Éditer l'utilisateur"
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-3xl flex flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95 max-h-[92dvh]"
      >
        <div className="sticky top-0 z-10 bg-gradient-to-r from-sky-600 via-sky-500 to-cyan-500 px-5 py-4 sm:px-6 flex items-center justify-between">
          <div>
            <h3 className="text-white text-lg sm:text-xl font-bold tracking-tight">
              {user
                ? "Modifier l'utilisateur"
                : "Créer / inviter un utilisateur"}
            </h3>
            <p className="text-white/90 text-xs sm:text-sm">
              Renseignez les informations du compte et choisissez les pages
              administratives autorisées.
            </p>
          </div>

          <button
            onClick={onClose}
            className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/15 hover:bg-white/25 transition text-white"
            aria-label="Fermer"
            type="button"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Nom
              </label>
              <input
                name="nom"
                type="text"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2"
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Prénom
              </label>
              <input
                type="text"
                value={prenom}
                onChange={(e) => setPrenom(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2"
                required
              />
              <p className="text-xs text-slate-500 mt-1">
                Cet email sera utilisé pour la connexion et l’envoi
                d’éventuelles invitations.
              </p>
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Rôle
              </label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2"
                required
              >
                <option value="">Sélectionner un rôle</option>
                {ROLES.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-800/40 p-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Pages autorisées du tableau de bord
                </h4>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Pour un administrateur, l’accès est total. Pour les autres
                  rôles, cochez les pages que l’utilisateur pourra consulter et
                  modifier.
                </p>
              </div>

              {!isAdmin && !loadingModules && availableModules.length > 0 && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleSelectAllModules}
                    className="px-3 py-1.5 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-xs hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    Tout cocher
                  </button>
                  <button
                    type="button"
                    onClick={handleUnselectAllModules}
                    className="px-3 py-1.5 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-xs hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    Tout décocher
                  </button>
                </div>
              )}
            </div>

            {isAdmin ? (
              <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
                Ce rôle dispose automatiquement d’un accès complet à toutes les
                pages de l’administration.
              </div>
            ) : loadingModules ? (
              <div className="mt-4 space-y-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-11 rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse"
                  />
                ))}
              </div>
            ) : availableModules.length === 0 ? (
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
                Aucun module disponible n’a été récupéré depuis l’API.
              </div>
            ) : (
              <>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {availableModules.map((module) => {
                    const moduleKey = module.key || module.module_key;
                    const moduleLabel = module.label || module.module_key;
                    const checked = selectedModuleKeys.includes(moduleKey);

                    return (
                      <label
                        key={moduleKey}
                        className={`flex items-start gap-3 rounded-xl border px-4 py-3 cursor-pointer transition ${
                          checked
                            ? "border-sky-300 bg-sky-50 dark:border-sky-500/40 dark:bg-sky-500/10"
                            : "border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900/60"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                          checked={checked}
                          onChange={() => toggleModule(moduleKey)}
                        />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                            {moduleLabel}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">
                            Clé module : {moduleKey}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>

                <div className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                  Si aucune page n’est cochée, l’utilisateur pourra se connecter
                  mais ne verra aucun module administratif.
                </div>
              </>
            )}
          </div>

          <div className="sticky bottom-0 z-10 -mx-4 sm:-mx-6 mt-6">
            <div className="pointer-events-none h-6 bg-gradient-to-t from-white/95 dark:from-slate-900/95 to-transparent" />
            <div className="pointer-events-auto bg-white/95 dark:bg-slate-900/95 border-t border-slate-200/70 dark:border-slate-800/70 px-4 sm:px-6 py-3 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-xl bg-slate-100 text-slate-800 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 inline-flex items-center gap-2"
              >
                <FaTimes /> Annuler
              </button>

              <button
                type="submit"
                className="px-4 py-2 rounded-xl inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white"
                title="Enregistrer"
              >
                <FaSave /> Enregistrer
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default UserModal;